import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/api';
import { Child } from '../types';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { WidgetBridgePlugin } from 'capacitor-widget-bridge';
import HomeScreen from './HomeScreen';
import ChildrenScreen from './ChildrenScreen';
import ChildActivityScreen from './ChildActivityScreen';
import CalendarScreen from './CalendarScreen';
import ClubsScreen from './ClubsScreen';
import ConnectionsScreen from './ConnectionsScreen';
import ProfileScreen from './ProfileScreen';
import AdminScreen from './AdminScreen';
import OnboardingFlow from './OnboardingFlow';
import './Dashboard.css';

type Tab = 'home' | 'children' | 'activity' | 'calendar' | 'clubs' | 'connections' | 'profile' | 'admin';

interface DashboardProps {
  initialTab?: Tab;
}

const Dashboard: React.FC<DashboardProps> = ({ initialTab = 'home' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Determine initial active tab based on current URL
  const getInitialTab = (): Tab => {
    const path = location.pathname;
    if (path === '/' || path === '/home') {
      return 'home';
    } else if (path.startsWith('/children') && (path.includes('/activities') || params.childUuid)) {
      return 'children';
    } else if (path === '/calendar') {
      return 'calendar';
    } else if (path === '/clubs') {
      return 'clubs';
    } else if (path === '/connections') {
      return 'connections';
    } else if (path === '/admin') {
      return 'admin';
    }
    return initialTab;
  };

  const [activeTab, setActiveTab] = useState<Tab>(getInitialTab());

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [cameFromActivity, setCameFromActivity] = useState(false);
  const [cameFromOnboardingToConnections, setCameFromOnboardingToConnections] = useState(false);
  const [shouldRestoreActivityCreation, setShouldRestoreActivityCreation] = useState(false);
  const [activityCreationChildUuid, setActivityCreationChildUuid] = useState<string | null>(null);
  const [activityCreationActivityUuid, setActivityCreationActivityUuid] = useState<string | null>(null);
  // Removed navigationKey - no longer needed since we removed popstate handler
  const [calendarInitialDate, setCalendarInitialDate] = useState<string | undefined>(undefined);
  const [calendarInitialViewMode, setCalendarInitialViewMode] = useState<'month' | 'week'>('week');
  const [children, setChildren] = useState<any[]>([]);
  const [childrenRefreshTrigger, setChildrenRefreshTrigger] = useState<number>(0);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Finishing onboarding closes the modal and navigates before the
  // onboarding_completed PUT lands. Anything that reloads children in that
  // window reads a stale `false` from the profile and reopens the flow, which
  // is what sent a finished user straight back round the loop. A ref settles
  // it locally the moment they finish, whatever the server has caught up to.
  const onboardingCompletedRef = useRef(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [connectionsWaitingCount, setConnectionsWaitingCount] = useState(0);
  // SchoolsBuddy integration state for welcome modal
  const [schoolsBuddyEmail, setSchoolsBuddyEmail] = useState('');
  const [schoolsBuddyPassword, setSchoolsBuddyPassword] = useState('');
  const [schoolsBuddySchoolName, setSchoolsBuddySchoolName] = useState('');
  const [schoolsBuddySchoolUrl, setSchoolsBuddySchoolUrl] = useState('');
  const [showSchoolsBuddyPassword, setShowSchoolsBuddyPassword] = useState(false);
  const [searchingSchools, setSearchingSchools] = useState(false);
  const [schoolSearchResults, setSchoolSearchResults] = useState<any[]>([]);
  const [selectedSchoolsBuddySite, setSelectedSchoolsBuddySite] = useState('');
  const [savingSchoolsBuddy, setSavingSchoolsBuddy] = useState(false);
  const [schoolsBuddyConnected, setSchoolsBuddyConnected] = useState(false);
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const apiService = ApiService.getInstance();

  // App resume trigger - increments when app comes to foreground
  const [appResumeTrigger, setAppResumeTrigger] = useState(0);

  // Listen for app resume events (when app comes to foreground)
  useEffect(() => {
    if (Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android') {
      const setupAppStateListener = async () => {
        try {
          await App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
              console.log('📱 App resumed - triggering refresh');
              // Trigger refresh for HomeScreen
              setAppResumeTrigger(prev => prev + 1);
              // Refresh iOS widget to fetch latest activities
              apiService.refreshWidget();
            }
          });
          console.log('✅ App state listener registered');
        } catch (error) {
          console.log('ℹ️ Could not register app state listener:', error);
        }
      };
      setupAppStateListener();

      return () => {
        App.removeAllListeners();
      };
    }
  }, []);

  // Check if user should see onboarding (new user with no children and no server-side onboarding_completed flag)
  // Load actual connection count from API
  const loadConnectionCount = async () => {
    try {
      const response = await apiService.getConnectionRequests();
      if (response.success && response.data) {
        const pendingCount = response.data.filter((req: any) => req.status === 'pending').length;
        setConnectionsWaitingCount(pendingCount);
        return pendingCount;
      }
    } catch (error) {
      console.error('Failed to load connection count:', error);
    }
    return 0;
  };

  // SchoolsBuddy helper functions for welcome modal
  const handleSearchSchoolsBuddySchool = async () => {
    try {
      if (!schoolsBuddySchoolName.trim()) {
        alert('Please enter a school name');
        return;
      }

      setSearchingSchools(true);
      const response = await apiService.searchSchoolsBuddySchool(schoolsBuddySchoolName.trim());

      if (response.success && response.data.sites && response.data.sites.length > 0) {
        setSchoolSearchResults(response.data.sites);
        // Automatically select the first site if there's only one
        if (response.data.sites.length === 1) {
          setSelectedSchoolsBuddySite(response.data.sites[0].url);
          setSchoolsBuddySchoolUrl(response.data.sites[0].url);
        }
      } else {
        setSchoolSearchResults([]);
        alert('No schools found. Please try a different name.');
      }
    } catch (error: any) {
      console.error('School search error:', error);
      alert(`Search failed: ${error.message || 'Unknown error'}`);
    } finally {
      setSearchingSchools(false);
    }
  };

  const handleConnectSchoolsBuddy = async () => {
    try {
      setSavingSchoolsBuddy(true);

      if (!schoolsBuddyEmail || !schoolsBuddyPassword || !schoolsBuddySchoolName || !schoolsBuddySchoolUrl) {
        alert('Please fill in all fields and select a school URL');
        return;
      }

      console.log('🔗 Linking SchoolsBuddy account from welcome modal...');

      const response = await apiService.linkSchoolsBuddyAccount(
        schoolsBuddyEmail,
        schoolsBuddyPassword,
        schoolsBuddySchoolName,
        schoolsBuddySchoolUrl
      );

      if (response.success && response.data) {
        console.log('✅ SchoolsBuddy linking successful!');
        setSchoolsBuddyConnected(true);
        // Clear form fields
        setSchoolsBuddyEmail('');
        setSchoolsBuddyPassword('');
        setSchoolsBuddySchoolName('');
        setSchoolsBuddySchoolUrl('');
        setSchoolSearchResults([]);
        setSelectedSchoolsBuddySite('');
      } else {
        alert(`Failed to link SchoolsBuddy account: ${response.error || 'Unknown error'}\n\nPlease check your credentials and try again.`);
      }
    } catch (error: any) {
      console.error('SchoolsBuddy linking error:', error);
      alert(`Error: ${error.message || 'Failed to link account'}`);
    } finally {
      setSavingSchoolsBuddy(false);
    }
  };

  const checkForNewUserOnboarding = async (childrenData: any[]) => {
    if (onboardingCompletedRef.current) {
      return;
    }
    try {
      // Skip onboarding if user has skeleton merge connections waiting AND hasn't dismissed the welcome modal yet
      if (user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting && !user.skeleton_welcome_dismissed && !showOnboarding) {
        console.log('ℹ️ Skipping onboarding check - skeleton merge user with connections waiting');
        return;
      }

      // Get user profile to check onboarding status
      const profileResponse = await apiService.getProfile();
      const onboardingCompleted = profileResponse.data?.onboarding_completed || false;

      const userEmail = user?.email;
      const shouldShowOnboarding = !onboardingCompleted;
      const forceOnboarding = localStorage.getItem('force_onboarding') === 'true';
      const isMobile = /Mobi|Android/i.test(navigator.userAgent);

      if (shouldShowOnboarding || forceOnboarding) {
        setShowOnboarding(true);

        // Clear force flag after using it
        if (forceOnboarding) {
          localStorage.removeItem('force_onboarding');
        }
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      // Fallback to not showing onboarding on error
    }
  };

  const handleOnboardingComplete = async () => {
    console.log('🔵 handleOnboardingComplete called - closing onboarding modal');

    // Before anything can navigate or reload: they are done.
    onboardingCompletedRef.current = true;

    // Close modal immediately, don't wait for API
    setShowOnboarding(false);
    setActiveTab('home');
    navigate('/');

    try {
      // Just send onboarding_completed flag, no other fields needed
      await apiService.updateProfile({
        onboarding_completed: true
      });
      console.log('✅ Onboarding marked as completed on server');

      // After onboarding is complete, check if this is a skeleton merge user
      // and show the welcome popup if they have connections waiting
      if (user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting) {
        // Load actual connection count before showing popup
        loadConnectionCount().then(() => {
          setShowWelcomePopup(true);
        });
      }
    } catch (error) {
      console.error('❌ Failed to mark onboarding as completed on server:', error);
      // Modal already closed above, so just show welcome popup if needed
      if (user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting) {
        loadConnectionCount().then(() => {
          setShowWelcomePopup(true);
        });
      }
    }
  };

  const handleNavigateToProfile = () => {
    console.log('🟢 Dashboard: handleNavigateToProfile called');
    // Temporarily hide onboarding modal (will come back after parent added)
    setShowOnboarding(false);
    console.log('🟢 Dashboard: Setting activeTab to profile');
    setActiveTab('profile');
    console.log('🟢 Dashboard: Navigating to /profile');
    navigate('/profile');

    // Trigger the add parent modal after a short delay
    setTimeout(() => {
      console.log('🟢 Dashboard: Timeout triggered, looking for Add Parent button');
      // Find all buttons and filter by text content to get the Add Parent button
      const buttons = Array.from(document.querySelectorAll('button'));
      const addParentButton = buttons.find(btn => btn.textContent?.includes('+ Add Parent')) as HTMLButtonElement;
      console.log('🟢 Dashboard: Found button:', addParentButton);
      if (addParentButton) {
        console.log('🟢 Dashboard: Clicking add parent button');
        addParentButton.click();
      } else {
        console.log('🔴 Dashboard: No Add Parent button found!');
      }
    }, 500);
  };

  const handleParentAdded = () => {
    // After parent is successfully added, return to onboarding flow (not skeleton welcome)
    setActiveTab('home'); // Reset to home tab
    setShowOnboarding(true); // Always show onboarding flow after adding parent
  };

  // Navigate to ProfileScreen and open the term planner modal for SchoolsBuddy imported planners
  interface TermPlannerInfo {
    childUuid: string;
    childName: string;
    termPlannerUuid: string;
  }

  const handleNavigateToTermPlanner = (termPlanners: TermPlannerInfo[], childrenWithoutPlanners: any[]) => {
    console.log('🟢 Dashboard: handleNavigateToTermPlanner called', { termPlanners, childrenWithoutPlanners });
    // Close onboarding modal
    setShowOnboarding(false);
    // Navigate to profile with state to open term planner modal
    setActiveTab('profile');
    navigate('/profile', {
      state: {
        openTermPlanner: true,
        termPlanners: termPlanners,  // All term planners to review
        childrenWithoutPlanners: childrenWithoutPlanners,  // Children that need manual setup
        currentTermPlannerIndex: 0,  // Start with the first one
        fromOnboarding: true  // Flag to track onboarding context
      }
    });
  };

  // Listen for parent added events globally
  useEffect(() => {
    const handleParentAddedEvent = () => {
      handleParentAdded();
    };

    const handleSkeletonChildCompletedEvent = () => {
      setActiveTab('home'); // Reset to home tab
      setShowWelcomePopup(true); // Show welcome modal
    };

    const handleChildCreatedEvent = async () => {
      setActiveTab('home'); // Reset to home tab

      // Only show onboarding if user hasn't completed it yet
      try {
        const profileResponse = await apiService.getProfile();
        const onboardingCompleted = profileResponse.data?.onboarding_completed || false;

        if (!onboardingCompleted && !onboardingCompletedRef.current) {
          setShowOnboarding(true); // Show onboarding flow only if not completed
        }
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Don't show onboarding on error
      }
    };

    const handleChildUpdatedEvent = async () => {
      setActiveTab('home'); // Reset to home tab

      // Check user's onboarding and welcome status before showing any modals
      try {
        const profileResponse = await apiService.getProfile();
        const onboardingCompleted = profileResponse.data?.onboarding_completed || false;
        const skeletonWelcomeDismissed = profileResponse.data?.skeleton_welcome_dismissed || false;

        // Check if this is a skeleton merge user - show unified welcome modal instead of onboarding
        if (user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting && !skeletonWelcomeDismissed) {
          setShowWelcomePopup(true);
        } else if (!onboardingCompleted && !onboardingCompletedRef.current) {
          setShowOnboarding(true); // Show onboarding flow for regular users who haven't completed it
        }
        // Otherwise, do nothing - user has completed onboarding/welcome, just navigate to home
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        // Don't show any modals on error
      }
    };

    const handleAddChildCanceled = () => {
      // When add child modal is canceled from onboarding, restore onboarding modal
      setActiveTab('home');
      navigate('/');
      if (!onboardingCompletedRef.current) {
        setShowOnboarding(true);
      }
    };

    const handleTermPlannerSetupComplete = async () => {
      // After term planner setup, show the connections step in onboarding
      console.log('🟢 Dashboard: termPlannerSetupComplete event received');
      // Set localStorage so OnboardingFlow resumes at connections step
      localStorage.setItem('onboarding_current_step', 'connections');
      // Navigate to home and show onboarding modal
      navigate('/');
      setActiveTab('home');
      setShowOnboarding(true);
    };

    window.addEventListener('parentAdded', handleParentAddedEvent);
    window.addEventListener('skeletonChildCompleted', handleSkeletonChildCompletedEvent);
    window.addEventListener('childCreated', handleChildCreatedEvent);
    window.addEventListener('childUpdated', handleChildUpdatedEvent);
    window.addEventListener('addChildCanceled', handleAddChildCanceled);
    window.addEventListener('termPlannerSetupComplete', handleTermPlannerSetupComplete);

    return () => {
      window.removeEventListener('parentAdded', handleParentAddedEvent);
      window.removeEventListener('skeletonChildCompleted', handleSkeletonChildCompletedEvent);
      window.removeEventListener('childCreated', handleChildCreatedEvent);
      window.removeEventListener('childUpdated', handleChildUpdatedEvent);
      window.removeEventListener('addChildCanceled', handleAddChildCanceled);
      window.removeEventListener('termPlannerSetupComplete', handleTermPlannerSetupComplete);
    };
  }, []);

  // Load children on mount
  useEffect(() => {
    const loadChildren = async () => {
      try {
        const response = await apiService.getChildren();
        if (response.success && response.data) {
          const childrenData = Array.isArray(response.data)
            ? response.data
            : (response.data as any)?.data || [];
          setChildren(childrenData);

          // Check if this is a new user who should see onboarding
          await checkForNewUserOnboarding(childrenData);
        }
      } catch (error) {
        console.error('Failed to load children:', error);
      }
    };

    loadChildren();

    // Listen for children data changes from ChildrenScreen
    const handleChildrenDataChanged = () => {
      loadChildren();
    };
    
    window.addEventListener('childrenDataChanged', handleChildrenDataChanged);
    
    return () => {
      window.removeEventListener('childrenDataChanged', handleChildrenDataChanged);
    };
  }, []);

  // Check for skeleton merge welcome flow
  useEffect(() => {
    // Don't show welcome popup if user has dismissed it
    if (user?.skeleton_welcome_dismissed) {
      return;
    }

    // Don't interfere if onboarding is already showing
    if (showOnboarding) {
      return;
    }

    if (user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting) {
      // Load actual connection count from API instead of using stale data
      loadConnectionCount().then((actualCount) => {
        // Skip onboarding flow and go directly to unified welcome modal
        setShowOnboarding(false); // Ensure onboarding is hidden
        setShowWelcomePopup(true);
      });
    }
  }, [user, showOnboarding]);

  // Get UUIDs from URL params - React Router handles this automatically
  const activityUuid = params.activityUuid;
  const childUuid = params.childUuid;

  // Resolve child UUID from URL to internal ID (only when path includes /activities)
  useEffect(() => {
    const path = location.pathname;

    // Only set selectedChildId if we're on a child activities path
    if (childUuid && children.length > 0 && path.includes('/activities')) {
      const child = children.find(c => c.uuid === childUuid);
      if (child) {
        setSelectedChildId(child.uuid);
      } else {
        setSelectedChildId(null);
      }
    } else if (path === '/children') {
      // Clear selected child when on main children page
      setSelectedChildId(null);
    } else if (path.includes('/activities') && !childUuid) {
      // Handle case where we're on activities path but no childUuid
      setSelectedChildId(null);
    }
  }, [childUuid, children, location.pathname]);

  // Removed all navigation detection and component reset logic
  // Let React Router handle all navigation naturally

  // Sync activeTab with URL
  useEffect(() => {
    const path = location.pathname;

    if (path === '/' || path === '/home') {
      setActiveTab('home');
    } else if (path.startsWith('/activity/')) {
      // Activity route - don't change activeTab, it should remain as calendar from previous navigation
    } else if (path.startsWith('/children')) {
      // Children routes still exist for direct access from calendar/home view buttons
      // but don't highlight any tab since there's no My Children tab anymore
      // Extract child UUID from URL if present (from /children/:childUuid/activities)
      const childUuid = params.childUuid;
      if (childUuid && path.includes('/activities')) {
        // Find child by UUID and set the ID for internal state
        // This will be set when children are loaded
      } else if (path === '/children') {
        setSelectedChildId(null);
      }
    } else if (path === '/calendar') {
      setActiveTab('calendar');
    } else if (path === '/clubs') {
      setActiveTab('clubs');
    } else if (path === '/connections') {
      setActiveTab('connections');
    } else if (path === '/profile') {
      setActiveTab('profile');
    } else if (path === '/admin') {
      setActiveTab('admin');
    }
  }, [location.pathname, params.childUuid]);



  const handleMobileNavClick = (tab: Tab) => {
    if (tab === 'connections') {
      handleNavigateToConnections();
    } else {
      // Trigger refresh for ChildrenScreen if switching to children tab
      if (tab === 'children') {
        setChildrenRefreshTrigger(Date.now());
      }
      navigate(`/${tab}`);
      if (tab !== 'calendar') {
        setCalendarInitialDate(undefined);
        setCalendarInitialViewMode('week');
      }
    }
    setMobileMenuOpen(false);
  };

  const handleChildSelection = (child: any) => {
    // Navigate to child-specific URL using UUID
    const targetURL = `/children/${child.uuid}/activities`;

    // Use React Router navigate with replace: false to create history entry
    navigate(targetURL, {
      replace: false,
      state: { fromPath: '/children' }
    });
    setCalendarInitialDate(undefined);
  };

  const handleNavigateToConnectionsFromActivity = (isInActivityCreation: boolean = false) => {
    setCameFromActivity(true);
    setShouldRestoreActivityCreation(isInActivityCreation);
    setCalendarInitialDate(undefined);
    // Store current child UUID and activity UUID when navigating to connections from activity creation
    if (isInActivityCreation && childUuid) {
      setActivityCreationChildUuid(childUuid);
      if (activityUuid) {
        setActivityCreationActivityUuid(activityUuid);
      }
    }
    setActiveTab('connections'); // Use activeTab instead of navigate
    // Scroll to top when navigating to connections
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigateToConnections = () => {
    setCameFromActivity(false);
    setCalendarInitialDate(undefined);
    navigate('/connections');
  };

  const renderContent = () => {
    // Derive the actual active tab from current pathname to avoid stale state during navigation
    const currentPath = location.pathname;
    let actualTab: Tab = activeTab;
    if (currentPath === '/' || currentPath === '/home') {
      actualTab = 'home';
    } else if (currentPath.startsWith('/activity/')) {
      actualTab = 'activity';
    } else if (currentPath.startsWith('/children')) {
      actualTab = 'children';
    } else if (currentPath === '/calendar') {
      actualTab = 'calendar';
    } else if (currentPath === '/clubs') {
      actualTab = 'clubs';
    } else if (currentPath === '/connections') {
      actualTab = 'connections';
    } else if (currentPath === '/profile') {
      actualTab = 'profile';
    } else if (currentPath === '/admin') {
      actualTab = 'admin';
    }

    switch (actualTab) {
      case 'home':
        return <HomeScreen
          appResumeTrigger={appResumeTrigger}
          onNavigateToActivity={(child, activity) => {
            const activityUuid = (activity as any).activity_uuid || (activity as any).uuid;
            const activityPath = `/activity/${child.uuid}/${activityUuid}`;

            console.log('🟢 DASHBOARD onNavigateToActivity:', {
              childUuid: child.uuid,
              childName: child.name,
              activityInput: activity,
              extractedActivityUuid: activityUuid,
              navigationPath: activityPath,
              hasActivityName: !!(activity as any).name,
              activityKeys: Object.keys(activity || {})
            });

            // DIRECT navigation from home to activity detail
            navigate(activityPath, {
              replace: false,
              state: {
                fromPath: '/home',
                activity: activity,
                invitationStatus: {
                  childUuid: child.uuid,
                  isPendingInvitation: (activity as any).isPendingInvitation || false,
                  isAcceptedInvitation: (activity as any).isAcceptedInvitation || false,
                  isDeclinedInvitation: (activity as any).isDeclinedInvitation || false,
                  invitationUuid: (activity as any).invitation_uuid || (activity as any).invitationUuid
                }
              }
            });
          }}
          onNavigateToChildCalendar={(child, activityDate) => {
            // If no activity date provided, use today's date
            const dateToUse = activityDate || new Date().toISOString().split('T')[0];
            // Set calendar to weekly view and navigate to the activity's week
            setCalendarInitialDate(dateToUse);
            setCalendarInitialViewMode('week');
            setActiveTab('calendar');
            navigate('/calendar');
            // Scroll to top of page
            window.scrollTo(0, 0);
          }}
          onNavigateToChildren={() => {
            navigate('/children');
          }}
          onNavigateToTermPlanner={(childUuid) => {
            setActiveTab('profile');
            navigate('/profile', {
              state: {
                createNewTermPlanner: true,
                childUuid
              }
            });
            window.scrollTo(0, 0);
          }}
        />;
      case 'calendar':
        return <CalendarScreen
          initialDate={calendarInitialDate}
          initialViewMode={calendarInitialViewMode}
          onNavigateToActivity={(child, activity, calendarState) => {
            console.log('🟠 DASHBOARD NAVIGATE - activity before navigation:', activity);
            console.log('🟠 activity_source in activity:', (activity as any).activity_source);
            const activityUuid = (activity as any).activity_uuid || (activity as any).uuid;
            const activityPath = `/activity/${child.uuid}/${activityUuid}`;

            // Save calendar state BEFORE navigation so it's available when user returns
            if (calendarState) {
              setCalendarInitialDate(calendarState.currentMonth.toISOString().split('T')[0]);
              setCalendarInitialViewMode(calendarState.viewMode);
            }

            // Use setTimeout to ensure state updates complete before navigation
            // This prevents double history entries from state updates
            setTimeout(() => {
              // DIRECT navigation from calendar to activity detail
              navigate(activityPath, {
                replace: false,
                state: {
                  fromPath: '/calendar', // Mark that this came from calendar
                  activity: activity, // Pass the full activity object
                  calendarReturnState: calendarState ? {
                    currentMonth: calendarState.currentMonth.toISOString().split('T')[0],
                    viewMode: calendarState.viewMode
                  } : undefined,
                  // Parent activity support
                  isParentActivity: calendarState?.isParentActivity || false,
                  parentUuid: calendarState?.parentUuid,
                  // Set when the rep picked a class rather than a child, so the
                  // form opens with that class already chosen.
                  preselectClassUuid: (calendarState as any)?.preselectClassUuid,
                  parentName: (child as any).name, // Parent name passed as child.name
                  invitationStatus: {
                    childUuid: child.uuid, // Include child UUID for matching
                    isPendingInvitation: (activity as any).isPendingInvitation || false,
                    isAcceptedInvitation: (activity as any).isAcceptedInvitation || false,
                    isDeclinedInvitation: (activity as any).isDeclinedInvitation || false,
                    // For pending invitations from batch endpoint, uuid IS the invitation UUID
                    invitationUuid: (activity as any).isPendingInvitation
                      ? ((activity as any).uuid || (activity as any).invitation_uuid || (activity as any).invitationUuid)
                      : ((activity as any).invitation_uuid || (activity as any).invitationUuid)
                  }
                }
              });
            }, 0);
          }}
        />;
      case 'clubs':
        try {
          return <ClubsScreen />;
        } catch (error) {
          console.error('❌ Error rendering ClubsScreen:', error);
          return (
            <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
              <h2>❌ Error Loading Clubs</h2>
              <p>There was an error loading the clubs screen. Please try refreshing the page.</p>
              <button onClick={() => window.location.reload()}>Reload Page</button>
            </div>
          );
        }
      case 'connections':
        return <ConnectionsScreen
          cameFromActivity={cameFromActivity}
          returnToActivityUrl={shouldRestoreActivityCreation ? `/children/${childUuid}/activities/new` : undefined}
          onForceTabUpdate={(tab) => {
            setActiveTab(tab as any);
          }}
          onReturnToActivity={() => {
            setCameFromActivity(false);
            setCalendarInitialDate(undefined);

            // Trigger refresh of connections data when returning from connections screen
            setChildrenRefreshTrigger(Date.now());

            if (shouldRestoreActivityCreation) {
              // Coming from activity creation - return to activity creation form with UUID-based URL
              const newActivityUrl = `/children/${childUuid}/activities/new`;

              // Navigate immediately - the URL sync effect will handle setting activeTab
              navigate(newActivityUrl, { replace: false });
            } else {
              // Coming from existing activity editing - return to specific activity
              setActiveTab('home'); // Navigate back to home screen
              // shouldRestoreActivityCreation is false, so it will show the activity list or specific activity
            }
          }}
          cameFromOnboarding={cameFromOnboardingToConnections}
          onContinueOnboarding={() => {
            setCameFromOnboardingToConnections(false);
            // Set localStorage to continue to add_to_home_screen step
            localStorage.setItem('onboarding_current_step', 'add_to_home_screen');
            setActiveTab('home');
            navigate('/');
            setShowOnboarding(true);
          }}
        />;
      case 'activity':
        // Check if this is a parent activity (from navigation state)
        const navState = location.state as any;
        const isParentActivity = navState?.isParentActivity;

        let activityChild: Child | undefined;
        if (isParentActivity && navState?.parentUuid) {
          // Create a pseudo-child object for parent activities
          activityChild = {
            uuid: navState.parentUuid,
            name: navState.parentName || 'Parent',
            id: 0, // Not used for parent activities
          } as Child;
        } else {
          activityChild = children.find(c => c.uuid === params.childUuid);
        }

        if (!activityChild) {
          return <div style={{ padding: '20px', textAlign: 'center' }}>Child not found</div>;
        }
        return <ChildActivityScreen
          child={activityChild}
          onBack={() => {
            // Use browser back to return to previous page
            navigate(-1);
          }}
          initialActivityUuid={params.activityUuid}
        />;
      case 'profile':
        return <ProfileScreen />;
      case 'admin':
        return isAdmin ? <AdminScreen /> : <div>Access Denied</div>;
      default:
        return <ChildrenScreen 
          onNavigateToCalendar={() => {
            setCalendarInitialDate(undefined);
            setActiveTab('calendar');
          }} 
          onNavigateToConnections={() => setActiveTab('connections')}
          refreshTrigger={childrenRefreshTrigger}
        />;
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <button 
              className="mobile-menu-btn"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="burger-line"></span>
              <span className="burger-line"></span>
              <span className="burger-line"></span>
            </button>
            <img 
              src="/cluubz-logo-white.png" 
              alt="Cluubz" 
              className="dashboard-logo"
            />
          </div>
          <div className="header-user">
            <button
              type="button"
              className="welcome-text"
              onClick={() => navigate('/profile')}
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}
              title="Open profile"
            >
              Welcome, {user?.username}!
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-body">
        {/* Desktop Navigation */}
        <nav className="dashboard-nav desktop-nav">
          <button
            className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
            onClick={() => {
              setCalendarInitialDate(undefined);
              navigate('/home');
            }}
          >
            <span className="nav-label">Home</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'calendar' ? 'active' : ''}`}
            onClick={() => {
              // Only reset calendar state if NOT coming from a child activity page
              // This preserves the month/week view when returning from activity detail
              if (!location.pathname.includes('/children/') || !location.pathname.includes('/activities/')) {
                setCalendarInitialDate(undefined);
                setCalendarInitialViewMode('week');
              }
              navigate('/calendar');
            }}
          >
            <span className="nav-label">Calendar</span>
          </button>
          
          <button
            className={`nav-item ${activeTab === 'clubs' ? 'active' : ''}`}
            onClick={() => {
              setCalendarInitialDate(undefined);
              navigate('/clubs');
            }}
          >
            <span className="nav-label">Browse Clubs</span>
          </button>
          
          <button
            className={`nav-item ${activeTab === 'connections' ? 'active' : ''}`}
            onClick={handleNavigateToConnections}
          >
            <span className="nav-label">Connections</span>
          </button>

          <button
            className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => {
              setCalendarInitialDate(undefined);
              navigate('/profile');
            }}
          >
            <span className="nav-label">Profile</span>
          </button>
          
          {isAdmin && (
            <button
              className={`nav-item ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => {
                setCalendarInitialDate(undefined);
                navigate('/admin');
              }}
            >
              <span className="nav-label">Admin</span>
              <span className="admin-badge">
                {user?.role === 'super_admin' ? 'SA' : 'A'}
              </span>
            </button>
          )}
        </nav>

        {/* Mobile Navigation Overlay */}
        {mobileMenuOpen && (
          <>
            <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}></div>
            <nav className="mobile-nav">
              <button
                className={`nav-item ${activeTab === 'home' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('home')}
              >
                <span className="nav-label">Home</span>
              </button>

              <button
                className={`nav-item ${activeTab === 'calendar' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('calendar')}
              >
                <span className="nav-label">Calendar</span>
              </button>
              
              <button
                className={`nav-item ${activeTab === 'clubs' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('clubs')}
              >
                <span className="nav-label">Browse Clubs</span>
              </button>
              
              <button
                className={`nav-item ${activeTab === 'connections' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('connections')}
              >
                <span className="nav-label">Connections</span>
              </button>

              <button
                className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => handleMobileNavClick('profile')}
              >
                <span className="nav-label">Profile</span>
              </button>
              
              {isAdmin && (
                <button
                  className={`nav-item ${activeTab === 'admin' ? 'active' : ''}`}
                  onClick={() => handleMobileNavClick('admin')}
                >
                  <span className="nav-label">Admin</span>
                  <span className="admin-badge">
                    {user?.role === 'super_admin' ? 'SA' : 'A'}
                  </span>
                </button>
              )}
            </nav>
          </>
        )}

        <main className="dashboard-content">
          {renderContent()}
        </main>
      </div>


      {/* Onboarding Flow for New Users */}
      {showOnboarding && (() => {
        console.log('🎓 RENDERING ONBOARDING FLOW - showOnboarding is TRUE');
        console.log('🔍 showWelcomePopup:', showWelcomePopup);
        return true;
      })() && (
        <OnboardingFlow
          onComplete={handleOnboardingComplete}
          onNavigateToProfile={handleNavigateToProfile}
          onParentAdded={handleParentAdded}
          connectionsWaitingCount={connectionsWaitingCount}
          onNavigateToTermPlanner={handleNavigateToTermPlanner}
          onNavigateToConnections={() => {
            setShowOnboarding(false);
            setCameFromOnboardingToConnections(true);
            setActiveTab('connections');
            navigate('/connections');
          }}
          onNavigateToChildren={() => {
            // Temporarily hide onboarding modal (will come back after child added)
            setShowOnboarding(false);
            setActiveTab('profile');
            navigate('/profile');
            // Trigger add child modal from Profile screen after a short delay
            setTimeout(() => {
              const addChildButton = document.querySelector('.add-btn') as HTMLButtonElement;
              if (addChildButton) {
                addChildButton.click();
              }
            }, 500);
          }}
        />
      )}

      {/* Unified Welcome Modal for Skeleton Merge Users */}
      {showWelcomePopup && (() => {
        console.log('🎉 RENDERING SKELETON WELCOME MODAL - showWelcomePopup is TRUE');
        return true;
      })() && (
        <div className="modal-overlay skeleton-modal-overlay" onClick={() => setShowWelcomePopup(false)}>
          <div className="welcome-popup unified-welcome" onClick={(e) => e.stopPropagation()}>
            <div className="welcome-popup-header">
              <h2>🎉 Welcome to Cluubz!</h2>
              <button
                className="close-btn"
                onClick={() => setShowWelcomePopup(false)}
              >
                ×
              </button>
            </div>
            <div className="welcome-popup-content">
              {connectionsWaitingCount > 0 ? (
                <>
                  <p>Great news! You have <strong>{connectionsWaitingCount} connection{connectionsWaitingCount !== 1 ? 's' : ''}</strong> waiting for you.</p>
                  <p>Other parents have already reached out to connect with you while you were setting up your account.</p>
                </>
              ) : (
                <>
                  <p>Welcome to your Cluubz account!</p>
                  <p>You can now start organizing activities and connecting with other families.</p>
                </>
              )}

              {/* Show skeleton children for completion */}
              {(() => {
                // For skeleton merge users, show ALL children for completion
                const isSkeletonMergeUser = user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting;

                if (isSkeletonMergeUser && children.length > 0) {
                  return true;
                }

                // Fallback for non-skeleton users - use original detection logic
                const skeletonChildren = children.filter(child => {
                  const isSkeleton = child.is_skeleton;
                  const hasSkeletonMarkers = !child.first_name || !child.last_name || child.first_name === '' || child.last_name === '';
                  return isSkeleton || hasSkeletonMarkers;
                });
                return skeletonChildren.length > 0;
              })() && (
                <div className="skeleton-children-section">
                  <h3>Complete Your Children's Profiles</h3>
                  <p>Please complete the profiles for these children:</p>
                  {(() => {
                    // For skeleton merge users, show ALL children
                    const isSkeletonMergeUser = user && user.skeletonMerge && user.skeletonMerge.hasConnectionsWaiting;

                    if (isSkeletonMergeUser) {
                      return children; // Show ALL children for skeleton merge users
                    }

                    // For non-skeleton users, filter by skeleton markers
                    return children.filter(child => {
                      const isSkeleton = child.is_skeleton;
                      const hasSkeletonMarkers = !child.first_name || !child.last_name || child.first_name === '' || child.last_name === '';
                      return isSkeleton || hasSkeletonMarkers;
                    });
                  })().map(child => (
                    <div key={child.uuid} className="skeleton-child-card">
                      <div className="skeleton-child-info">
                        <span className="skeleton-child-name">
                          {child.display_name || child.name || 'Unnamed Child'}
                        </span>
                        {child.birth_date && (
                          <span className="skeleton-child-age">
                            Age: {new Date().getFullYear() - new Date(child.birth_date).getFullYear()}
                          </span>
                        )}
                      </div>
                      <button
                        className="option-button primary"
                        onClick={() => {
                          setShowWelcomePopup(false);
                          // Small delay to ensure modal closes before navigation
                          setTimeout(() => {
                            setActiveTab('profile');
                            navigate(`/profile?editChild=${child.uuid}`);
                          }, 100);
                        }}
                      >
                        Complete Profile
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* SchoolsBuddy Integration Setup */}
              <div className="schoolsbuddy-setup-section" style={{
                marginTop: '20px',
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📚</span> Connect SchoolsBuddy
                </h3>
                {schoolsBuddyConnected ? (
                  <div style={{ color: '#28a745', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span>✓</span> SchoolsBuddy connected successfully!
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '14px', color: '#666', margin: '0 0 12px 0' }}>
                      Connect your SchoolsBuddy account to automatically import your children's clubs and activities.
                    </p>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>Email:</label>
                      <input
                        type="email"
                        value={schoolsBuddyEmail}
                        onChange={(e) => setSchoolsBuddyEmail(e.target.value)}
                        placeholder="your.email@example.com"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>Password:</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showSchoolsBuddyPassword ? 'text' : 'password'}
                          value={schoolsBuddyPassword}
                          onChange={(e) => setSchoolsBuddyPassword(e.target.value)}
                          placeholder="Enter your password"
                          style={{
                            width: '100%',
                            padding: '10px',
                            paddingRight: '40px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => setShowSchoolsBuddyPassword(!showSchoolsBuddyPassword)}
                          style={{
                            position: 'absolute',
                            right: '8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '16px',
                            padding: '4px'
                          }}
                        >
                          {showSchoolsBuddyPassword ? '👁️' : '👁️‍🗨️'}
                        </button>
                      </div>
                    </div>

                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: 500 }}>School Name:</label>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <input
                          type="text"
                          value={schoolsBuddySchoolName}
                          onChange={(e) => setSchoolsBuddySchoolName(e.target.value)}
                          placeholder="e.g., Berkhamsted School"
                          style={{
                            flex: 1,
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                        <button
                          type="button"
                          onClick={handleSearchSchoolsBuddySchool}
                          disabled={searchingSchools || !schoolsBuddySchoolName.trim()}
                          style={{
                            padding: '10px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: searchingSchools || !schoolsBuddySchoolName.trim() ? 'not-allowed' : 'pointer',
                            opacity: searchingSchools || !schoolsBuddySchoolName.trim() ? 0.6 : 1,
                            whiteSpace: 'nowrap',
                            fontSize: '14px'
                          }}
                        >
                          {searchingSchools ? '...' : '🔍'}
                        </button>
                      </div>
                    </div>

                    {schoolSearchResults.length > 0 && (
                      <div style={{
                        marginBottom: '12px',
                        maxHeight: '150px',
                        overflowY: 'auto',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: 'white'
                      }}>
                        {schoolSearchResults.map((site: any, index: number) => (
                          <div
                            key={index}
                            onClick={() => {
                              setSelectedSchoolsBuddySite(site.url);
                              setSchoolsBuddySchoolUrl(site.url);
                            }}
                            style={{
                              padding: '10px',
                              borderBottom: index < schoolSearchResults.length - 1 ? '1px solid #eee' : 'none',
                              backgroundColor: selectedSchoolsBuddySite === site.url ? '#d4edda' : 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ fontSize: '13px' }}>{site.name || site.url}</span>
                            {selectedSchoolsBuddySite === site.url && <span style={{ color: '#28a745' }}>✓</span>}
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={handleConnectSchoolsBuddy}
                      disabled={savingSchoolsBuddy || !schoolsBuddyEmail || !schoolsBuddyPassword || !schoolsBuddySchoolUrl}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: savingSchoolsBuddy || !schoolsBuddyEmail || !schoolsBuddyPassword || !schoolsBuddySchoolUrl ? 'not-allowed' : 'pointer',
                        opacity: savingSchoolsBuddy || !schoolsBuddyEmail || !schoolsBuddyPassword || !schoolsBuddySchoolUrl ? 0.6 : 1,
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    >
                      {savingSchoolsBuddy ? '⏳ Connecting...' : '🔗 Connect SchoolsBuddy'}
                    </button>

                    <p style={{ fontSize: '12px', color: '#888', margin: '8px 0 0 0', textAlign: 'center' }}>
                      🔒 Your credentials are encrypted and stored securely
                    </p>
                  </>
                )}
              </div>

              <div className="welcome-popup-actions">
                <button
                  className="btn-primary"
                  onClick={async () => {
                    // Mark skeleton welcome as dismissed in database
                    try {
                      await apiService.dismissSkeletonWelcome();
                      // Refresh user to get updated skeleton_welcome_dismissed flag
                      await refreshUser();
                    } catch (error) {
                      console.error('Failed to dismiss skeleton welcome:', error);
                    }

                    setShowWelcomePopup(false);

                    // Check if there are still skeleton children with incomplete profiles
                    const hasIncompleteSkeletonChildren = (() => {
                      if (!children || children.length === 0) return false;

                      return children.some(child => {
                        const isSkeleton = child.is_skeleton === true;
                        const hasSkeletonMarkers =
                          !child.birth_date ||
                          child.birth_date === '1970-01-01' ||
                          child.birth_date === '0001-01-01';
                        return isSkeleton || hasSkeletonMarkers;
                      });
                    })();

                    if (hasIncompleteSkeletonChildren) {
                      // Still have skeleton children - navigate to refresh
                      navigate('/home');
                      // Reload the page to refresh skeleton children data
                      setTimeout(() => {
                        window.location.reload();
                      }, 100);
                    } else {
                      // All skeleton children completed - transition to regular onboarding
                      setShowOnboarding(true);
                    }
                  }}
                >
                  Continue Setup
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;